import { useState, type ReactElement } from 'react';
import { api, DELIVERY_MODES, type DeliveryMode, type UserView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { useAsync } from '../shared/lib/use-async';
import { DataTable, type Column } from '../shared/ui/DataTable';
import { FilterChips } from '../shared/ui/primitives';

const ROLES = ['admin', 'dispatcher', 'driver'] as const;

type RoleFilter = (typeof ROLES)[number] | 'all';

/**
 * Пользователи и их отделы (ADR-0030).
 *
 * Отдел — галочка на режим доставки: диспетчер курьерской службы видит только
 * экспресс, диспетчер дистрибуции — только опт, администратор группы — оба. Смена
 * действует сразу, без повторного входа: сервер читает отделы на каждом запросе.
 *
 * Галочка сохраняется сразу, а не кнопкой: распределение по отделам — несколько
 * щелчков на человека, и забытая кнопка «Сохранить» оставила бы диспетчера в чужом
 * отделе до первого инцидента.
 *
 * Курьеры — не отдельный раздел, а фильтр роли: это те же пользователи. Логист видит
 * список без правки отделов — распределяет администратор.
 */
export function UsersPage({
  canEditModes,
  role,
  onRole,
}: {
  readonly canEditModes: boolean;
  readonly role: string | null;
  readonly onRole: (role: string | null) => void;
}): ReactElement {
  const messages = useMessages();
  const users = useAsync(() => api.users(), []);
  const filter: RoleFilter = ROLES.includes(role as (typeof ROLES)[number])
    ? (role as RoleFilter)
    : 'all';
  const all = users.data?.data ?? [];
  const rows = filter === 'all' ? all : all.filter((user) => user.role === filter);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (user: UserView, mode: DeliveryMode): Promise<void> => {
    setError(null);

    const modes = user.modes.includes(mode)
      ? user.modes.filter((item) => item !== mode)
      : [...user.modes, mode];

    try {
      await api.setUserModes(user.id, modes);
      users.reload();
    } catch {
      setError(messages.cabinet.users.saveFailed);
    }
  };

  const columns: readonly Column<UserView>[] = [
    { key: 'name', title: messages.cabinet.users.name, render: (row) => row.full_name },
    {
      key: 'phone',
      title: messages.cabinet.users.phone,
      width: '160px',
      render: (row) => <span className="num">{row.phone}</span>,
    },
    {
      key: 'role',
      title: messages.cabinet.users.role,
      width: '140px',
      render: (row) => messages.cabinet.users.roles[row.role],
    },
    {
      key: 'modes',
      title: messages.cabinet.users.departments,
      width: '360px',
      render: (row) =>
        !canEditModes ? (
          row.modes.map((mode) => messages.cabinet.modes[mode]).join(' · ') ||
          messages.cabinet.users.noDepartment
        ) : (
          <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
            {DELIVERY_MODES.map((mode) => (
              <label
                key={mode}
                style={{ display: 'flex', gap: 'var(--sp-1)', alignItems: 'center' }}
              >
                <input
                  type="checkbox"
                  checked={row.modes.includes(mode)}
                  onChange={() => void toggle(row, mode)}
                />
                <span>{messages.cabinet.modes[mode]}</span>
              </label>
            ))}
            {row.modes.length === 0 ? (
              <span style={{ color: 'var(--warn)' }}>{messages.cabinet.users.noDepartment}</span>
            ) : null}
          </div>
        ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.nav.users}</h1>

      <FilterChips
        active={filter}
        onSelect={(code) => onRole(code === 'all' ? null : code)}
        items={(['all', ...ROLES] as const).map((code) => ({
          code,
          label: messages.cabinet.users.filter[code],
          count: code === 'all' ? all.length : all.filter((user) => user.role === code).length,
        }))}
      />

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        emptyText={messages.cabinet.common.empty}
      />
    </div>
  );
}
